/**
 * A failed API call, carrying the HTTP status AND the parsed response body.
 *
 * Before this, `fetchApi` threw a status-less `Error`, so no page could tell a
 * 403 (render the restricted state) from a 404 (nothing there) from a 500 (the
 * server broke) — each read as the same red "failed to load". The status is what
 * a page branches on; the body is kept because this API answers with *data* on
 * several failures (a ProblemDetails `detail` naming the refused parameter, a
 * conflict naming what it conflicts with).
 *
 * Mirrors Cartographer's `utils/apiErrors.ts` — the two SPAs are copies, not a
 * shared package, and should stay alike. Lives in its own module, importing
 * nothing from `services/api.ts` or `src/auth`, because `auth/msalInstance.ts`
 * reads `window.location.origin` at module load: anything that must be importable
 * under vitest's node environment (`utils/format.ts` above all) cannot drag MSAL
 * in behind it.
 */
export class ApiError extends Error {
  readonly status: number
  /** Parsed JSON body when the server sent one, else the raw text, else null. */
  readonly body: unknown
  /** The ProblemDetails `title`, when the body was one. */
  readonly title: string | null
  /** The ProblemDetails `detail`, when the body was one — usually the actionable reason. */
  readonly detail: string | null

  constructor(
    status: number,
    message: string,
    body: unknown,
    title: string | null = null,
    detail: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.title = title
    this.detail = detail
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/**
 * A bearer-only request that was NOT SENT, because this session has no Entra token.
 *
 * Every Observatory write (run a job, clear a halt, handle a signal, cancel a run, acknowledge or restore a
 * Data Comb finding) is pinned to JwtBearer on the server and refuses the shared API key even in Development,
 * so `fetchApi(url, { auth: 'bearer-only' })` throws this before calling `fetch` rather than falling back to
 * the key. It carries no status because there was no
 * response: `errorKind` reports it as 'sign-in-required', never as a server's 401.
 */
export class BearerRequiredError extends Error {
  constructor() {
    super('Not sent: this action accepts only an Entra sign-in.')
    this.name = 'BearerRequiredError'
  }
}

export function isBearerRequiredError(error: unknown): error is BearerRequiredError {
  return error instanceof BearerRequiredError
}

/**
 * The actionable text of an RFC 7807 problem detail: `detail` first, then `title`.
 *
 * Detail before title, because ASP.NET's validation ProblemDetails carries the
 * generic "One or more validation errors occurred." as its title and the reason
 * in its detail — preferring the title is how a refused trigger used to read as
 * nothing a user could act on.
 */
export function problemDetailText(body: unknown): string | null {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return null
  const record = body as Record<string, unknown>
  const detail = typeof record.detail === 'string' ? record.detail : null
  const title = typeof record.title === 'string' ? record.title : null
  return detail ?? title
}
